import type { ColDef } from "ag-grid-community";

type ComparatorRowData = {
	rowType?: "latest" | "history";
	__parentIndex?: number;
	historyIndex?: number;
	parentKey?: string;
	scmIDNum?: number;
	itemNumber?: number;
	fiscalYear?: number;
	parentFilterData?: {
		fiscalYear?: number;
	};
};

type ComparatorNode = {
	data?: ComparatorRowData;
	rowIndex?: number | null;
	sourceRowIndex?: number;
};

const getNodeData = (node: unknown): ComparatorRowData | undefined => {
	if (!node || typeof node !== "object" || !("data" in node)) {
		return undefined;
	}

	const data = (node as { data?: unknown }).data;
	return data && typeof data === "object" ? (data as ComparatorRowData) : undefined;
};

const getNodeIndex = (node: unknown, data: ComparatorRowData | undefined): number | undefined => {
	if (typeof data?.__parentIndex === "number" && Number.isFinite(data.__parentIndex)) {
		return data.__parentIndex;
	}

	// sourceRowIndex is stable while AG Grid is recalculating rowIndex during a refresh.
	const sourceRowIndex = (node as ComparatorNode | undefined)?.sourceRowIndex;
	if (data?.rowType === "history" && typeof sourceRowIndex === "number" && sourceRowIndex >= 0) {
		return sourceRowIndex;
	}

	const rowIndex = (node as ComparatorNode | undefined)?.rowIndex;
	return typeof rowIndex === "number" && Number.isFinite(rowIndex) ? rowIndex : undefined;
};

const hasExplicitParentIndex = (data: ComparatorRowData | undefined): boolean =>
	typeof data?.__parentIndex === "number" && Number.isFinite(data.__parentIndex);

const getGroupKey = (data: ComparatorRowData | undefined): string | undefined => {
	if (data?.rowType === "history") {
		return data.parentKey;
	}

	if (data?.scmIDNum == null || data.itemNumber == null) {
		return undefined;
	}

	return `${data.scmIDNum}::${data.itemNumber}`;
};

const compareGroupOrder = (indexA: number, indexB: number, isDescending?: boolean): number => {
	const diff = indexA - indexB;
	return isDescending ? -diff : diff;
};

const compareGroupMembers = (
	dataA: ComparatorRowData | undefined,
	dataB: ComparatorRowData | undefined,
	isDescending?: boolean,
): number | undefined => {
	const aIsHistory = dataA?.rowType === "history";
	const bIsHistory = dataB?.rowType === "history";

	if (!(aIsHistory || bIsHistory)) {
		return undefined;
	}

	if (aIsHistory && bIsHistory) {
		return compareGroupOrder(dataA.historyIndex ?? 0, dataB.historyIndex ?? 0, isDescending);
	}

	// The parent must remain first even when AG Grid reverses the comparator for DESC.
	if (aIsHistory) {
		return isDescending ? -1 : 1;
	}
	return isDescending ? 1 : -1;
};

const compareGroupedRows = (
	dataA: ComparatorRowData | undefined,
	dataB: ComparatorRowData | undefined,
	idxA: number | undefined,
	idxB: number | undefined,
	isDescending?: boolean,
): number | undefined => {
	const aIsHistory = dataA?.rowType === "history";
	const bIsHistory = dataB?.rowType === "history";
	const groupKeyA = getGroupKey(dataA);
	const sameGroup =
		groupKeyA !== undefined && groupKeyA === getGroupKey(dataB) && (aIsHistory || bIsHistory);
	const hasGroupOrder =
		(hasExplicitParentIndex(dataA) && hasExplicitParentIndex(dataB)) || aIsHistory || bIsHistory;

	if (sameGroup) {
		const memberComparison = compareGroupMembers(dataA, dataB, isDescending);
		if (memberComparison !== undefined) {
			return memberComparison;
		}
	}

	if (hasGroupOrder && idxA !== undefined && idxB !== undefined && idxA !== idxB) {
		// Use one ordering key for every cross-group comparison. Mixing API indexes
		// for history rows with column values for parents is not transitive and lets
		// AG Grid place a history row between two different parents.
		return compareGroupOrder(idxA, idxB, isDescending);
	}

	if (hasGroupOrder && (aIsHistory || bIsHistory) && idxA === idxB) {
		return compareGroupMembers(dataA, dataB, isDescending);
	}

	return undefined;
};

const compareValues = (valueA: unknown, valueB: unknown): number => {
	if (valueA == null && valueB == null) return 0;
	if (valueA == null) return -1;
	if (valueB == null) return 1;

	if (typeof valueA === "number" && typeof valueB === "number") return valueA - valueB;

	const aStr = String(valueA).toLowerCase();
	const bStr = String(valueB).toLowerCase();
	if (aStr < bStr) return -1;
	if (aStr > bStr) return 1;
	return 0;
};

/**
 * Keep history rows under their parent; grouped rows retain the API order while
 * AG Grid applies the active sort to ungrouped rows.
 */
export const groupAwareComparator = (
	valueA: unknown,
	valueB: unknown,
	nodeA: unknown,
	nodeB: unknown,
	isDescending?: boolean,
): number => {
	const dataA = getNodeData(nodeA);
	const dataB = getNodeData(nodeB);
	const idxA = getNodeIndex(nodeA, dataA);
	const idxB = getNodeIndex(nodeB, dataB);
	const groupedComparison = compareGroupedRows(dataA, dataB, idxA, idxB, isDescending);
	if (groupedComparison !== undefined) {
		return groupedComparison;
	}

	return compareValues(valueA, valueB);
};

// Risk statuses use business severity order instead of alphabetical order.
type GridComparator = NonNullable<ColDef["comparator"]>;

const compareFiscalYearGroups = (
	valueA: unknown,
	valueB: unknown,
	dataA: ComparatorRowData | undefined,
	dataB: ComparatorRowData | undefined,
	nodeA: unknown,
	nodeB: unknown,
	isDescending?: boolean,
): number => {
	const aIsHistory = dataA?.rowType === "history";
	const bIsHistory = dataB?.rowType === "history";

	if (!(aIsHistory || bIsHistory)) {
		return compareValues(valueA, valueB);
	}

	const parentYearA = aIsHistory ? dataA?.parentFilterData?.fiscalYear : valueA;
	const parentYearB = bIsHistory ? dataB?.parentFilterData?.fiscalYear : valueB;
	const yearComparison = groupAwareComparator(
		parentYearA,
		parentYearB,
		{ data: { rowType: "latest" } },
		{ data: { rowType: "latest" } },
		isDescending,
	);
	if (yearComparison !== 0) {
		return yearComparison;
	}

	const idxA = getNodeIndex(nodeA, dataA) ?? 0;
	const idxB = getNodeIndex(nodeB, dataB) ?? 0;
	if (idxA !== idxB) {
		return compareGroupOrder(idxA, idxB, isDescending);
	}

	return compareGroupMembers(dataA, dataB, isDescending) ?? 0;
};

/**
 * Sort fiscal-year groups by the latest row while keeping each history row
 * directly below its parent in the order returned by the history API.
 */
export const fiscalYearComparator: GridComparator = (
	valueA,
	valueB,
	nodeA,
	nodeB,
	isDescending,
) => {
	return compareFiscalYearGroups(
		valueA,
		valueB,
		getNodeData(nodeA),
		getNodeData(nodeB),
		nodeA,
		nodeB,
		isDescending,
	);
};

export const riskStatusComparator: GridComparator = (
	valueA,
	valueB,
	nodeA,
	nodeB,
	isDescending,
) => {
	const dataA = getNodeData(nodeA);
	const dataB = getNodeData(nodeB);
	if (
		dataA?.rowType === "history" ||
		dataB?.rowType === "history" ||
		(hasExplicitParentIndex(dataA) && hasExplicitParentIndex(dataB))
	) {
		return groupAwareComparator(valueA, valueB, nodeA, nodeB, isDescending);
	}

	const getStatusRank = (value: unknown): number => {
		switch (String(value ?? "").toUpperCase()) {
			case "LOW":
				return 0;
			case "MEDIUM":
				return 1;
			case "HIGH":
				return 2;
			default:
				return 3;
		}
	};

	const rankA = getStatusRank(valueA);
	const rankB = getStatusRank(valueB);

	if (rankA === rankB) return 0;
	if (rankA === 3 || rankB === 3) {
		// Keep unavailable statuses last in both ASC and DESC modes.
		return rankA === 3 ? (isDescending ? -1 : 1) : isDescending ? 1 : -1;
	}

	return rankA - rankB;
};
